import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@easyflow/ui-frontend/components/ui/toaster';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { useTenantValidation } from '@/hooks/useTenantValidation';
import { AppShell } from '@/layout/AppShell';
import { W3_MODULES } from '@/modules/registry';
import { DEFAULT_TENANT_SLUG } from '@/lib/utils';
import TenantNotFound from '@/pages/TenantNotFound';

const LoginPage = lazy(() => import('@/pages/Login'));
const DashboardPage = lazy(() => import('@/modules/dashboard/DashboardPage'));
const OrganizzazionePage = lazy(() => import('@/modules/organizzazione/OrganizzazionePage'));
const ComingSoonPage = lazy(() => import('@/pages/ComingSoon'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-w3-orange" />
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { slug } = useParams<{ slug: string }>();
  if (isLoading) return <FullPageSpinner />;
  if (!isAuthenticated) return <Navigate to={`/${slug ?? DEFAULT_TENANT_SLUG}/login`} replace />;
  return children;
}

function TenantProductBoundary({ children }: { children: React.ReactElement }) {
  const { slug } = useParams<{ slug: string }>();
  const { isLoading, isValid } = useTenantValidation(slug);

  if (isLoading) return <FullPageSpinner />;
  if (!isValid) return <TenantNotFound />;
  return children;
}

function RootRedirect() {
  const { tenantSlug, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <FullPageSpinner />;
  const slug = tenantSlug ?? DEFAULT_TENANT_SLUG;
  return <Navigate to={isAuthenticated ? `/${slug}/dashboard` : `/${slug}/login`} replace />;
}

/** Route dei moduli dal registry: pilota reali, gli altri placeholder. */
function moduleElement(moduleId: string): React.ReactElement {
  switch (moduleId) {
    case 'dashboard':
      return <DashboardPage />;
    case 'organizzazione':
      return <OrganizzazionePage />;
    default:
      return <ComingSoonPage />;
  }
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, '')}>
          <Suspense fallback={<FullPageSpinner />}>
            <Routes>
              <Route path="/" element={<RootRedirect />} />
              <Route
                path="/:slug/login"
                element={
                  <TenantProductBoundary>
                    <LoginPage />
                  </TenantProductBoundary>
                }
              />
              <Route
                path="/:slug"
                element={
                  <TenantProductBoundary>
                    <RequireAuth>
                      <AppShell />
                    </RequireAuth>
                  </TenantProductBoundary>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                {W3_MODULES.filter((m) => m.enabled).map((mod) => (
                  <Route key={mod.id} path={mod.route} element={moduleElement(mod.id)} />
                ))}
                <Route path="*" element={<Navigate to="dashboard" replace />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </QueryClientProvider>
  );
}
